/**
 * WorkspaceGuard - Route guard for workspace-required pages
 *
 * Shows workspace selector when:
 * - User navigates to a workspace-required page
 * - No workspace is currently selected
 * - User has workspaces available (or prompts to create one)
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { auth } from '../api/client';
import {
  Building2, FolderPlus, ArrowRight, Search, Check,
  ChevronRight, X, Loader
} from 'lucide-react';
import { useI18n } from '../i18n';

// Routes that REQUIRE a workspace to be selected
const WORKSPACE_REQUIRED_ROUTES = [
  '/scans',
  '/vulnerabilities',
  '/credentials',
  '/ad',
  '/reports',
  '/attack',
  '/equipment',
  '/enrichment',
];

// Routes that DON'T require workspace
const WORKSPACE_EXEMPT_ROUTES = [
  '/',
  '/dashboard',
  '/workspaces',
  '/account',
  '/pricing',
  '/credits',
  '/support',
  '/notifications',
  '/settings',
  '/admin',
  '/mitre-navigator',
  '/llm',
  '/antivirus',
  '/phishing',
  '/sessions',
  '/error',
  '/reports/generated',
];

const WorkspaceGuard = () => {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    hasWorkspace,
    workspaces,
    loading,
    switchWorkspace,
    fetchWorkspaces,
  } = useWorkspace();

  const [showCreatePrompt, setShowCreatePrompt] = useState(false);
  const [showSelectPrompt, setShowSelectPrompt] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [fetchingList, setFetchingList] = useState(false);
  const [error, setError] = useState(null);

  // Check if current route requires workspace
  const requiresWorkspace = () => {
    const path = location.pathname;
    return WORKSPACE_REQUIRED_ROUTES.some(route =>
      path === route || path.startsWith(route + '/')
    );
  };

  // Check if route is exempt from workspace requirement
  const isExemptRoute = () => {
    const path = location.pathname;
    return WORKSPACE_EXEMPT_ROUTES.some(route =>
      path === route || path.startsWith(route + '/')
    );
  };

  useEffect(() => {
    if (!auth.isAuthenticated() || !auth.isTokenValidClientSide() || loading) return;

    if (isExemptRoute()) {
      setShowCreatePrompt(false);
      setShowSelectPrompt(false);
      return;
    }

    if (requiresWorkspace() && !hasWorkspace) {
      setSearch('');
      setSelectedId(null);
      setError(null);

      if (workspaces.length === 0) {
        setFetchingList(true);
        fetchWorkspaces().then((ws) => {
          setFetchingList(false);
          if (ws && ws.length === 0) {
            setShowCreatePrompt(true);
            setShowSelectPrompt(false);
          } else if (ws && ws.length > 0) {
            setShowSelectPrompt(true);
            setShowCreatePrompt(false);
          }
        });
      } else if (workspaces.length === 0) {
        setShowCreatePrompt(true);
        setShowSelectPrompt(false);
      } else {
        setShowSelectPrompt(true);
        setShowCreatePrompt(false);
      }
    } else {
      setShowCreatePrompt(false);
      setShowSelectPrompt(false);
    }
  }, [location.pathname, hasWorkspace, loading, workspaces.length]);

  // Filter workspaces by search
  const filteredWorkspaces = useMemo(() => {
    if (!search.trim()) return workspaces;
    const term = search.toLowerCase();
    return workspaces.filter(ws =>
      ws.name?.toLowerCase().includes(term) ||
      ws.company_name?.toLowerCase().includes(term) ||
      ws.city?.toLowerCase().includes(term)
    );
  }, [search, workspaces]);

  const handleConfirm = async () => {
    if (!selectedId) return;
    setSwitching(true);
    setError(null);
    try {
      const success = await switchWorkspace(selectedId);
      if (success) {
        setShowSelectPrompt(false);
        window.location.reload();
      } else {
        setError('Erreur lors du changement de workspace');
      }
    } catch (err) {
      setError(err.message || 'Erreur lors du changement de workspace');
    } finally {
      setSwitching(false);
    }
  };

  const handleCreateWorkspace = () => {
    setShowCreatePrompt(false);
    setShowSelectPrompt(false);
    navigate('/workspaces?action=create');
  };

  const handleGoBack = () => {
    setShowCreatePrompt(false);
    setShowSelectPrompt(false);
    navigate('/dashboard');
  };

  // ── Create Workspace Prompt ──────────────────────────────────────────────
  if (showCreatePrompt) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        <div className="relative w-full max-w-md mx-4 bg-brand-dark border border-brand-green/30
                        rounded-xl shadow-2xl animate-in zoom-in-95 fade-in duration-200 p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-green/20 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-brand-green" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              {t('workspace.createRequired', 'Workspace requis')}
            </h2>
            <p className="text-gray-400 text-sm">
              {t('workspace.createRequiredDescription',
                'Create your first workspace to access this section.')}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <button
              onClick={handleCreateWorkspace}
              className="w-full flex items-center justify-center gap-2 px-6 py-3
                         bg-brand-green text-black font-medium rounded-lg hover:bg-brand-green/90 transition-colors"
            >
              <FolderPlus className="w-5 h-5" />
              {t('workspace.createNew', 'Create a workspace')}
            </button>
            <button
              onClick={handleGoBack}
              className="w-full flex items-center justify-center gap-2 px-6 py-3
                         bg-brand-black/50 text-gray-400 font-medium rounded-lg hover:text-white
                         hover:bg-brand-black/70 transition-colors border border-brand-green/20"
            >
              {t('common.goBack', 'Retour au dashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Select Workspace Prompt — with inline list + search ─────────────────
  if (showSelectPrompt) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

        <div className="relative w-full max-w-lg mx-4 bg-black border border-brand-green/30
                        rounded-xl shadow-2xl animate-in zoom-in-95 fade-in duration-200">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-brand-green/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-brand-green/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-brand-green" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {t('workspace.selectTitle', 'Workspace selection')}
                </h2>
                <p className="text-sm text-gray-400">
                  {workspaces.length} workspace{workspaces.length > 1 ? 's' : ''} disponible{workspaces.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="px-6 py-3 border-b border-brand-green/10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('workspace.searchPlaceholder', 'Rechercher un workspace...')}
                className="w-full pl-10 pr-4 py-2.5 bg-brand-black/50 border border-brand-green/20
                           rounded-lg text-white placeholder-gray-400
                           focus:outline-none focus:border-brand-green/50 text-sm"
                autoFocus
              />
            </div>
          </div>

          {/* Workspace List */}
          <div className="max-h-72 overflow-y-auto px-2 py-2">
            {fetchingList ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="w-6 h-6 text-brand-green animate-spin" />
              </div>
            ) : filteredWorkspaces.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Building2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">
                  {workspaces.length === 0
                    ? 'No workspace found'
                    : 'No results for this search'}
                </p>
              </div>
            ) : (
              filteredWorkspaces.map(ws => (
                <button
                  key={ws.id}
                  onClick={() => setSelectedId(ws.id)}
                  className={`w-full px-4 py-3 text-left rounded-lg transition-all mb-1
                             flex items-center gap-3 ${
                               selectedId === ws.id
                                 ? 'bg-brand-green/20 border border-brand-green/50'
                                 : 'hover:bg-brand-green/10 border border-transparent'
                             }`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                                  ${selectedId === ws.id ? 'bg-brand-green/30' : 'bg-brand-black/50'}`}>
                    <Building2 className={`w-4 h-4 ${selectedId === ws.id ? 'text-brand-green' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium text-sm truncate">{ws.name}</span>
                      {selectedId === ws.id && <Check className="w-4 h-4 text-brand-green flex-shrink-0" />}
                    </div>
                    {ws.company_name && (
                      <span className="text-xs text-gray-400 truncate block">
                        {ws.company_name}{ws.city && ` — ${ws.city}`}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      {ws.scans_count || 0} scan{(ws.scans_count || 0) > 1 ? 's' : ''}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mx-6 mb-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-brand-green/20 bg-brand-black/30">
            <button
              onClick={handleCreateWorkspace}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              <FolderPlus className="w-4 h-4" />
              {t('workspace.createNew', 'Create a workspace')}
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={handleGoBack}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                {t('common.goBack', 'Retour')}
              </button>
              <button
                onClick={handleConfirm}
                disabled={!selectedId || switching}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  selectedId && !switching
                    ? 'bg-brand-green text-black hover:bg-brand-green/90'
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }`}
              >
                {switching ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Chargement...
                  </>
                ) : (
                  <>
                    Continuer
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default WorkspaceGuard;
